import { cookieStorage } from '../utils/cookieStorage';
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

export type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  theme: ThemeMode;
  isDark: boolean;
  toggleTheme: () => void;
  setTheme: (theme: ThemeMode) => void;
  celestialTransition: 'sunset' | 'sunrise' | null;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_STORAGE_KEY = 'comarca_theme';

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<ThemeMode>(() => {
    try {
      const saved = cookieStorage.getItem(THEME_STORAGE_KEY);
      if (saved === 'dark' || saved === 'light') return saved;
    } catch {
      // Ignorar errores de acceso a cookieStorage
    }
    return 'light'; // Modo claro por defecto al entrar
  });

  const [celestialTransition, setCelestialTransition] = useState<'sunset' | 'sunrise' | null>(null);

  const isDark = theme === 'dark';

  useEffect(() => {
    try {
      cookieStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // Ignorar
    }

    const root = document.documentElement;
    if (isDark) {
      root.classList.add('dark');
      root.setAttribute('data-theme', 'dark');
      document.body.classList.add('dark-mode');
    } else {
      root.classList.remove('dark');
      root.setAttribute('data-theme', 'light');
      document.body.classList.remove('dark-mode');
    }
  }, [theme, isDark]);

  // Limpiar el efecto celestial después de 800ms
  useEffect(() => {
    if (!celestialTransition) return;
    const t = setTimeout(() => {
      setCelestialTransition(null);
    }, 800);
    return () => clearTimeout(t);
  }, [celestialTransition]);

  // toggleTheme se dispara EXCLUSIVAMENTE al presionar el interruptor de modo noche
  const toggleTheme = useCallback(() => {
    setThemeState(prev => {
      const next = prev === 'dark' ? 'light' : 'dark';
      setCelestialTransition(next === 'dark' ? 'sunset' : 'sunrise');
      return next;
    });
  }, []);

  const setTheme = useCallback((newTheme: ThemeMode) => {
    setThemeState(newTheme);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, isDark, toggleTheme, setTheme, celestialTransition }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme debe ser utilizado dentro de un ThemeProvider');
  }
  return context;
};

export default ThemeContext;
